import {
  type CodeInterpreterBlock,
  type CodeInterpreterTextBlock,
  type CodeInterpreterThinkingBlock,
  type CodeInterpreterToolCallBlock,
  type MessageContent
} from "@nexus/types"
import { DbEnum } from "@nexus/types/core"
import { hasRenderableContent, parseExecutionEventEnvelope } from "@nexus/types/domain"
import type { PayloadAction } from "@reduxjs/toolkit"

import type { OptimisticMessage } from "@/common/types/chat"

import type { PlaygroundChatState } from "../playgroundTypes"

/**
 * Adds a streaming token to an ongoing message stream.
 *
 * Accumulates tokens received during streaming into a single message content string.
 * Creates or updates the streaming message state for the given messageId.
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, and the token to append
 */
export const addStreamingToken = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; messageId: string; token: string }>
) => {
  const { chatId, messageId, token } = action.payload
  const chat = state.chats[chatId]

  if (!chat) {
    return
  }

  // Stop showing typing indicator as soon as we receive streaming content
  // This provides immediate feedback that the agent is responding
  chat.isSendingMessage = false

  const existing = chat.streamingMessages[messageId]
  const newContent = (existing?.content || "") + token

  chat.streamingMessages[messageId] = {
    content: newContent,
    isStreaming: true,
    isComplete: false,
    hasError: false,
    displayComplete: false
  }
}

/**
 * Completes a streaming message and adds it to the messages array.
 *
 * Finalizes a streaming message by:
 * 1. Using WebSocket content if provided (e.g., tool execution results)
 * 2. Falling back to accumulated streaming content (e.g., text responses)
 * 3. Skipping empty messages (AI messages with only tool_calls and no text)
 * 4. Detecting message type from content (TOOL for workflows, AI for text)
 *
 * Also handles messages that arrive without prior streaming
 * (e.g., TOOL messages executed and completed directly by the backend).
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, and optional content from WebSocket
 */
export const completeStreamingMessage = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    content?: string | MessageContent
    metaConversationId?: string
    contentType?: DbEnum.MessageContentType
    toolType?: DbEnum.ToolType
    toolCalls?: Array<{
      id: string
      type: string // "function" for OpenAI, provider-agnostic
      function: {
        name: string
        arguments: string
      }
    }>
    toolCallId?: string
    name?: string
  }>
) => {
  const {
    chatId,
    messageId,
    content: websocketContent,
    metaConversationId,
    contentType: providedContentType,
    toolType: providedToolType,
    toolCalls: providedToolCalls,
    toolCallId: providedToolCallId,
    name: providedName
  } = action.payload

  const chat = state.chats[chatId]

  if (!chat) return

  // Handle messages that arrive without prior streaming (e.g., TOOL messages)
  // The backend sends completeMessage directly without streaming tokens for tool executions
  const streamingData = chat.streamingMessages[messageId]
  const streamingContent = streamingData?.content

  // Use WebSocket content if provided (tool execution results),
  // otherwise use accumulated streaming content (text responses)
  const finalContent = websocketContent || streamingContent

  // Skip empty messages UNLESS they have toolCalls (e.g., AI messages that only contain
  // tool_calls like workflow invocations). Without this, ToolInput can never render in
  // real-time because the AI message carrying the toolCalls array is discarded.
  const hasToolCalls = providedToolCalls && providedToolCalls.length > 0
  if (!hasRenderableContent(finalContent) && !hasToolCalls) {
    if (streamingData) {
      streamingData.isStreaming = false
      streamingData.isComplete = true
    }
    return
  }

  const now = new Date()

  // The backend wraps a tool result as `{ text: JSON.stringify(envelope) }`,
  // and persists it bare on older rows. One shared parser reads both shapes and
  // returns a typed envelope, or null when the content is ordinary prose.
  const envelope = parseExecutionEventEnvelope(finalContent)

  // Use provided metadata if available, otherwise infer from content (backward compatibility)
  let messageType: DbEnum.MessageContentType = DbEnum.MessageContentType.AI
  let toolType: DbEnum.ToolType | null = null

  if (providedContentType) {
    // Use provided metadata (preferred)
    messageType = providedContentType
    toolType = providedToolType ?? null
  } else {
    // Fall back to content-based detection for backward compatibility. Routing
    // on the shared union covers every envelope kind, where the hand-kept list
    // this replaced covered four of five.
    const isWorkflowMessage = envelope !== null
    messageType = isWorkflowMessage ? DbEnum.MessageContentType.TOOL : DbEnum.MessageContentType.AI
    toolType = isWorkflowMessage ? DbEnum.ToolType.WORKFLOW : null
  }

  // Check if message already exists (for TOOL message status updates)
  const existingMessageIndex = chat.messages.findIndex((m) => m.id === messageId)
  const normalizedContent: MessageContent =
    typeof finalContent === "string"
      ? { text: finalContent }
      : (finalContent ?? { text: streamingContent ?? "" })

  if (existingMessageIndex !== -1) {
    // Update existing message (e.g., workflow status update from started → completed/failed)
    const existingMessage = chat.messages[existingMessageIndex]
    if (existingMessage) {
      existingMessage.content = normalizedContent
      existingMessage.updatedAt = now
      existingMessage.textContent =
        typeof finalContent === "string"
          ? finalContent
          : finalContent?.text || streamingContent || ""
      if (toolType !== null) {
        existingMessage.toolType = toolType
      }
      if (metaConversationId !== undefined) {
        existingMessage.metaConversationId = metaConversationId
      }
      if (providedToolCalls !== undefined) {
        existingMessage.toolCalls = providedToolCalls
      }
      if (providedToolCallId !== undefined) {
        existingMessage.toolCallId = providedToolCallId
      }
      if (providedName !== undefined) {
        existingMessage.name = providedName
      }
      // Sync statusInfos from parsed workflow content so DisplayWorkflow reflects the
      // latest status (COMPLETED/FAILED/CANCELLED) without waiting for a full loadMessages.
      // This prevents stale statusInfos (e.g. RUNNING) from overriding the correct status
      // when DisplayWorkflow prioritises statusInfos over message content.
      if (envelope?.workflowExecutionId !== undefined) {
        existingMessage.statusInfos = {
          workflowExecutionId: envelope.workflowExecutionId,
          workflowName: envelope.workflowName,
          workflowStatus: envelope.workflowStatus,
          iconUrl: envelope.iconUrl,
          message: envelope.message
        }
      } else if (
        typeof existingMessage.statusInfos === "object" &&
        existingMessage.statusInfos !== null &&
        "workflowStatus" in existingMessage.statusInfos &&
        (existingMessage.statusInfos as Record<string, unknown>).workflowStatus ===
          DbEnum.WorkflowExecutionStatus.RUNNING
      ) {
        // Non-JSON completion content (e.g. tool error plain text) — cannot determine
        // actual workflow status, so clear the stale RUNNING to let DisplayWorkflow
        // fall back to parsing content directly
        existingMessage.statusInfos = undefined
      }
    }
  } else {
    // Streaming completed - add as optimistic message, will be replaced by backend
    const completedMessage: OptimisticMessage = {
      id: messageId,
      chatId,
      content: normalizedContent,
      type: messageType,
      status: DbEnum.MessageStatus.PROCESSED,
      createdAt: now,
      updatedAt: now,
      name: providedName ?? undefined,
      agentId: chat.currentAgentId,
      files: [],
      statusInfos: undefined,
      textContent:
        typeof finalContent === "string"
          ? finalContent
          : finalContent?.text || streamingContent || "",
      toolType: toolType ?? undefined,
      toolCalls: providedToolCalls ?? undefined,
      metaConversationId: metaConversationId ?? undefined,
      toolCallId: providedToolCallId ?? undefined
    }
    chat.messages.push(completedMessage)
  }

  // Update streaming state if it exists (won't exist for direct TOOL messages)
  const currentStreamingData = chat.streamingMessages[messageId]
  if (currentStreamingData) {
    currentStreamingData.isStreaming = false
    currentStreamingData.isComplete = true
    // Clear the preparing files state - final message has arrived
    currentStreamingData.isPreparingFiles = false
  }

  // Ensure typing indicator is hidden when message completes
  chat.isSendingMessage = false
}

/**
 * Clears a streaming message from the state.
 *
 * Removes the streaming message entry entirely. Use this when the final message
 * has been received and the streaming state is no longer needed.
 *
 * @param state - The playground chat state
 * @param action - Contains chatId and messageId to clear
 */
export const clearStreamingMessage = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string; messageId: string }>
) => {
  const { chatId, messageId } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  delete chat.streamingMessages[messageId]
}

/**
 * Clears all streaming messages for a chat.
 *
 * Resets the entire streamingMessages map for the given chat to an empty object.
 * Use this after a sandbox reset so that error banners derived from streaming
 * state (e.g. session-blocking errors) are dismissed immediately.
 *
 * @param state - The playground chat state
 * @param action - Contains chatId whose streaming messages should be cleared
 */
export const clearChatStreamingMessages = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string }>
) => {
  const { chatId } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  chat.streamingMessages = {}
}

/**
 * Handles a streaming error for a message.
 *
 * Marks the streaming message as having an error and stops the streaming state.
 * The error details are stored for display to the user.
 *
 * NOTE: Creates the streaming message entry if it doesn't exist yet, because
 * errors can occur before any streaming tokens are received (e.g., code interpreter
 * session unavailable error occurs during message processing setup).
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, and error (object or string)
 */
export const streamingError = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    error: { code: string; message: string } | string
  }>
) => {
  const { chatId, messageId, error } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Normalize error to object format (support legacy string format)
  const normalizedError =
    typeof error === "string" ? { code: "STREAMING_ERROR", message: error } : error

  // Create streaming message entry if it doesn't exist yet
  // This handles errors that occur before any streaming tokens are received
  if (!chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId] = {
      content: "",
      isStreaming: false,
      isComplete: false,
      hasError: true,
      error: normalizedError,
      displayComplete: false
    }
  } else {
    chat.streamingMessages[messageId].hasError = true
    chat.streamingMessages[messageId].isStreaming = false
    chat.streamingMessages[messageId].error = normalizedError
  }

  // Stop sending state since processing failed
  chat.isSendingMessage = false
}

// =====================================================
// Code Interpreter Block Reducers (unified into streamingMessages)
// =====================================================

/**
 * Adds a new code interpreter block to the streaming message.
 * Blocks are stored in streamingMessages[messageId].blocks for interleaved display.
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, and block data
 */
export const addStreamingBlock = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    block: CodeInterpreterBlock
  }>
) => {
  const { chatId, messageId, block } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Stop showing typing indicator when we receive the first block
  chat.isSendingMessage = false

  // Initialize streaming message if needed
  if (!chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId] = {
      content: "",
      blocks: [],
      isStreaming: true,
      isComplete: false,
      hasError: false,
      displayComplete: false
    }
  }

  const streamingData = chat.streamingMessages[messageId]
  if (!streamingData.blocks) streamingData.blocks = []

  // Map incoming block to our internal format based on discriminated union type
  if (block.type === "thinking") {
    const thinkingBlock: CodeInterpreterThinkingBlock = {
      type: "thinking",
      id: block.id,
      content: block.content || "",
      isComplete: block.isComplete ?? false
    }
    streamingData.blocks.push(thinkingBlock)
  } else if (block.type === "tool_call") {
    const toolCallBlock: CodeInterpreterToolCallBlock = {
      type: "tool_call",
      id: block.id,
      toolId: block.toolId,
      name: block.name,
      input: block.input,
      isComplete: false
    }
    streamingData.blocks.push(toolCallBlock)
  } else if (block.type === "text") {
    const textBlock: CodeInterpreterTextBlock = {
      type: "text",
      id: block.id,
      content: block.content || ""
    }
    streamingData.blocks.push(textBlock)
  }
}

/**
 * Updates a block's content (for streaming thinking/text updates).
 * If the block doesn't exist yet (due to event ordering), creates it.
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, blockId, and content (accumulated, not delta)
 */
export const updateStreamingBlock = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    blockId: string
    content: string
  }>
) => {
  const { chatId, messageId, blockId, content } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  // Initialize streaming message if needed
  if (!chat.streamingMessages[messageId]) {
    chat.streamingMessages[messageId] = {
      content: "",
      blocks: [],
      isStreaming: true,
      isComplete: false,
      hasError: false,
      displayComplete: false
    }
  }

  const streamingData = chat.streamingMessages[messageId]
  if (!streamingData.blocks) streamingData.blocks = []

  const block = streamingData.blocks.find((b) => b.id === blockId)

  if (block && (block.type === "thinking" || block.type === "text")) {
    // Block exists - update its content
    block.content = content
  } else if (!block) {
    // Block doesn't exist yet (BLOCK_UPDATE arrived before BLOCK_START)
    // Infer the type from blockId prefix and create it
    const type = blockId.startsWith("thinking-") ? "thinking" : "text"
    if (type === "thinking") {
      streamingData.blocks.push({
        type: "thinking",
        id: blockId,
        content,
        isComplete: false
      })
    } else {
      streamingData.blocks.push({
        type: "text",
        id: blockId,
        content
      })
    }
  }
}

/**
 * Completes a block (typically a tool call with its result).
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, blockId, output, and isError
 */
export const completeStreamingBlock = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    blockId: string
    output?: string
    isError?: boolean
  }>
) => {
  const { chatId, messageId, blockId, output, isError } = action.payload
  const chat = state.chats[chatId]

  if (!chat?.streamingMessages[messageId]?.blocks) return

  const block = chat.streamingMessages[messageId].blocks?.find((b) => b.id === blockId)

  if (block && block.type === "tool_call") {
    block.output = output
    block.isError = isError
    block.isComplete = true
  } else if (block && block.type === "thinking") {
    block.isComplete = true
  }
}

/**
 * Marks code interpreter streaming as complete.
 * Keeps all blocks (including text) visible until completeStreamingMessage adds the final message.
 *
 * Note: We do NOT filter out text blocks here anymore. The streaming message with blocks
 * stays visible until completeStreamingMessage is called (when message:completed arrives),
 * which adds the final message to chat.messages. At that point, the streaming message is
 * no longer needed because the real message exists.
 *
 * @param state - The playground chat state
 * @param action - Contains chatId, messageId, content, and files
 */
export const completeCodeInterpreterStreaming = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chatId: string
    messageId: string
    content: string
    files?: string[]
  }>
) => {
  const { chatId, messageId, files } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  const streamingData = chat.streamingMessages[messageId]

  if (streamingData) {
    // Mark streaming as complete but keep all blocks visible
    // The blocks will be replaced when completeStreamingMessage adds the final message
    streamingData.isStreaming = false
    streamingData.isComplete = true
    // Only show "Preparing files..." if there are actually files to download
    // This avoids showing the indicator for simple text responses
    if (files && files.length > 0) {
      streamingData.isPreparingFiles = true
    }
  }

  // Mark session as active — unblocks the input after a successful execution.
  // Without this, useIsCodeInterpreterBlocked sees hasCodeInterpreterSession=false
  // and blocks the input since the chat has messages but no known session.
  chat.hasCodeInterpreterSession = true

  // Ensure typing indicator is hidden
  chat.isSendingMessage = false
}
